/**
 * Toast notification system.
 *
 * Provides global toast notifications with:
 * - Success, error, warning, and info variants
 * - Auto-dismiss with configurable duration
 * - Manual dismiss button
 * - Accessible with aria-live regions
 * - Stacked display in bottom-right corner
 *
 * Usage:
 * 1. Wrap app with <ToastProvider>
 * 2. Use useToast() hook to show toasts
 *
 * @example
 * const { toast, errorToast, successToast } = useToast();
 * successToast('Operation completed');
 * errorToast('Something went wrong');
 */

import * as React from 'react';

import { cn } from '@/lib/cn';

/**
 * Toast variant types.
 */
export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast action button.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * Toast data structure.
 */
export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  /** Optional action button */
  action?: ToastAction;
}

/**
 * Options for toast helper functions.
 */
export interface ToastOptions {
  duration?: number;
  action?: ToastAction;
}

/**
 * Toast context value.
 */
interface ToastContextValue {
  toasts: Toast[];
  addToast: (message: string, variant?: ToastVariant, duration?: number, action?: ToastAction) => void;
  removeToast: (id: string) => void;
  successToast: (message: string, options?: ToastOptions) => void;
  errorToast: (message: string, options?: ToastOptions) => void;
  warningToast: (message: string, options?: ToastOptions) => void;
  infoToast: (message: string, options?: ToastOptions) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

/**
 * Default toast duration in ms.
 */
const DEFAULT_DURATION = 5000;

/**
 * Generate unique toast ID.
 */
function generateToastId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Toast provider component.
 * Wrap your app with this to enable toasts.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback(
    (message: string, variant: ToastVariant = 'info', duration = DEFAULT_DURATION, action?: ToastAction) => {
      const id = generateToastId();
      const toast: Toast = {
        id,
        message,
        variant,
        duration,
        ...(action ? { action } : {}),
      };

      setToasts((prev) => [...prev, toast]);

      // Auto-dismiss after duration
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }
    },
    [],
  );

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const successToast = React.useCallback(
    (message: string, options?: ToastOptions) => addToast(message, 'success', options?.duration, options?.action),
    [addToast],
  );

  const errorToast = React.useCallback(
    (message: string, options?: ToastOptions) => addToast(message, 'error', options?.duration, options?.action),
    [addToast],
  );

  const warningToast = React.useCallback(
    (message: string, options?: ToastOptions) => addToast(message, 'warning', options?.duration, options?.action),
    [addToast],
  );

  const infoToast = React.useCallback(
    (message: string, options?: ToastOptions) => addToast(message, 'info', options?.duration, options?.action),
    [addToast],
  );

  const value = React.useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      successToast,
      errorToast,
      warningToast,
      infoToast,
    }),
    [toasts, addToast, removeToast, successToast, errorToast, warningToast, infoToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

/**
 * Hook to access toast functions.
 */
export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Toast container - renders all active toasts.
 */
function ToastContainer({
  toasts,
  onRemove,
}: {
  toasts: Toast[];
  onRemove: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-toast',
        'flex flex-col gap-2',
        'pointer-events-none',
      )}
      aria-live="polite"
      aria-label="Уведомления"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

/**
 * Individual toast item.
 */
function ToastItem({
  toast,
  onRemove,
}: {
  toast: Toast;
  onRemove: (id: string) => void;
}) {
  const variantStyles: Record<ToastVariant, string> = {
    success: 'bg-success-surface border-success-border text-success',
    error: 'bg-destructive/10 border-destructive/30 text-destructive',
    warning: 'bg-warning-surface border-warning-border text-warning',
    info: 'bg-info-surface border-info-border text-info',
  };

  const iconPaths: Record<ToastVariant, string> = {
    success: 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    error: 'M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z',
    warning: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z',
    info: 'M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
  };

  return (
    <div
      className={cn(
        'pointer-events-auto',
        'flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg',
        'min-w-[280px] max-w-sm',
        'animate-in slide-in-from-right-full fade-in duration-200',
        variantStyles[toast.variant],
      )}
      role="alert"
    >
      {/* Icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-5 flex-shrink-0"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={iconPaths[toast.variant]} />
      </svg>

      {/* Message */}
      <span className="flex-1 text-sm font-medium">{toast.message}</span>

      {/* Action button */}
      {toast.action ? (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick();
            onRemove(toast.id);
          }}
          className={cn(
            'flex-shrink-0 rounded-md px-2 py-1 text-xs font-medium',
            'bg-black/10 dark:bg-white/10',
            'hover:bg-black/20 dark:hover:bg-white/20',
            'focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1',
          )}
        >
          {toast.action.label}
        </button>
      ) : null}

      {/* Dismiss button */}
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className={cn(
          'flex-shrink-0 rounded-md p-1',
          'hover:bg-black/10 dark:hover:bg-white/10',
          'focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1',
        )}
        aria-label="Закрыть"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="size-4"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export type { ToastContextValue };
